import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import AnimationOnScroll from "react-animate-on-scroll";
import "animate.css/animate.min.css";
import BackArrow from "../assets/images/back-arrow.svg";
import NoImage from "../assets/images/no-image.svg";
import CameraIcon from "../assets/images/camera-icon.svg";
import CaptureIcon from "../assets/images/capture-icon.svg";
import CloseIcon from "../assets/images/close-icon.svg";
import RefreshIcon from "../assets/images/refresh-cw.svg";
import SuccessIcon from "../assets/images/success-filled.svg";
import FaceIcon from "../assets/icons/face.svg";
import NeckIcon from "../assets/icons/neck.svg";
import ChestIcon from "../assets/icons/chest.svg";
import BackViewIcon from "../assets/icons/back-view.svg";
import ArmsIcon from "../assets/icons/arms.svg";
import HandIcon from "../assets/icons/hand.svg";
import LegIcon from "../assets/icons/legs.svg";
import FeetIcon from "../assets/icons/footing.svg";
import OtherIcon from "../assets/icons/other.svg";
import {
  derma_api_key,
  derma_api_secret_key,
  sdkUnityURL,
} from "../utils/api-url-list";

const options = [
  { id: "face", label: "Face", image: FaceIcon },
  { id: "neck", label: "Neck", image: NeckIcon },
  { id: "chest", label: "Chest", image: ChestIcon },
  { id: "back", label: "Back", image: BackViewIcon },
  { id: "arms", label: "Arms", image: ArmsIcon },
  { id: "hands", label: "Hands", image: HandIcon },
  { id: "legs", label: "Legs", image: LegIcon },
  { id: "feet", label: "Feet", image: FeetIcon },
  { id: "others", label: "Others", image: OtherIcon },
];

const SkinAnalyzer = () => {
  const [image, setImage] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [spinDirection, setSpinDirection] = useState("clockwise");
  const [error, setError] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const startCamera = async () => {
    try {
      if (stream) stopCamera();

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });

      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  useEffect(() => {
    if (showCamera) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [showCamera, facingMode]);

  const handleCapture = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, 200, 200);
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setImage(dataUrl);
    setShowCamera(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imgUrl = URL.createObjectURL(file);
      setImage(imgUrl);
    }
  };

  const toggleCamera = () => {
    setSpinning(true);
    setSpinDirection((prev) =>
      prev === "clockwise" ? "anticlockwise" : "clockwise"
    );
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    setTimeout(() => setSpinning(false), 1000);
  };

  const handleReupload = () => {
    setImage(null);
    setShowCamera(false);
    setSelected(null);
    setError("");
  };

  const getBase64Image = async (img) => {
    if (img.startsWith("data:image")) {
      const [meta, base64] = img.split(",");
      const ext = meta.match(/data:image\/(.*);base64/)[1];
      return { base64, ext };
    } else {
      const blob = await fetch(img).then((res) => res.blob());
      const ext = blob.type.split("/")[1]; // e.g., "jpeg", "png"
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result.split(",")[1];
          resolve({ base64, ext });
        };
        reader.readAsDataURL(blob);
      });
    }
  };

  const handleAnalyze = async () => {
    if (!selected || !image) return;

    setLoading(true);

    try {
      const { base64, ext } = await getBase64Image(image);

      const payload = {
        api_key: derma_api_key,
        employee_id: "9830374972",
        type: selected,
        enc_image: base64,
        file_ext: ext,
      };

      const response = await fetch(sdkUnityURL("/derma/skin-assessment"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: derma_api_secret_key,
        },
        body: JSON.stringify(payload),
      });

      const resultJSON = await response.json();
      if (resultJSON?.statusCode?.toString().startsWith("2"))
        navigate("/derma-result", {
          state: {
            selectedArea: selected,
            image,
            analysis: resultJSON.result,
          },
        });
      else throw new Error(resultJSON?.message ?? "Failed to Fetch Result");
    } catch (err) {
      setLoading(false);
      setError(err.message || "Failed to Fetch Result");
      console.error(err);
    }
  };
  console.log("error", error);

  return (
    <section className="pt-2 flex flex-col">
      <div className="flex items-center justify-between px-6 mb-6 w-full relative z-50">
        <Link
          to={-1}
          className="w-9 h-9 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="Back" />
        </Link>
        <h2 className="text-xl font-medium text-white">
          Derma <span className="text-secondary">Ai</span>
        </h2>
        <div></div>
      </div>

      <h3 className="text-base font-semibold text-white mb-5 text-center relative z-50">
        New Assessment
      </h3>

      <div className="circle-blur w-[75%] h-64 mx-auto mt-0 blur-[90px] absolute left-0 right-0"></div>

      <div className="px-6 w-full mx-auto relative z-50">
        <div className="bg-[#1D1D1D]/70 rounded-[20px] px-6 pt-6 pb-5 mb-3 relative z-50">
          <h2 className="text-base font-semibold text-[#8AB2DF] text-center">
            Capture Image
          </h2>

          {!image && (
            <div className="mb-4 flex flex-col justify-center gap-4 mt-10">
              <div className="w-20 h-20 rounded-full bg-[#17191f] flex justify-center items-center mx-auto">
                <img src={NoImage} className="w-7 h-7" alt="" />
              </div>
              <p className="text-xs font-light text-white/75 text-center mb-8">
                No image captured yet
              </p>
              <div className="flex flex-row justify-between items-center">
                <button
                  onClick={() => setShowCamera(true)}
                  className="bg-white text-primary text-xs font-medium px-2 h-9 rounded-full w-[50%] flex justify-center items-center"
                >
                  <img src={CameraIcon} className="mr-2" alt="" /> Open Camera
                </button>
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="text-white px-2 h-9 rounded-full w-[45%] border border-white/30 text-xs font-medium"
                >
                  Upload Image
                </button>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {image && (
            <div className="mt-6">
              <img
                src={image}
                alt="Preview"
                className="mx-auto rounded-xl w-[200px] h-[200px]"
              />
              <div className="flex justify-between mt-6 mb-4">
                <button
                  onClick={handleReupload}
                  className="text-white text-xs font-medium px-3 rounded-full w-[48%] border border-white h-9 flex items-center justify-center"
                >
                  Retake
                </button>
                <div className="text-primary text-xs font-medium px-3 h-9 rounded-full bg-white flex items-center justify-center">
                  <img src={SuccessIcon} className="mr-2" alt="" /> Image Ready
                </div>
              </div>
            </div>
          )}

          {showCamera && (
            <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
              <div className="relative bg-white p-5 rounded-[15px] shadow-md w-10/12">
                <h3 className="text-base font-semibold mb-4 text-primary text-center">
                  Capture Skin Photo
                </h3>
                <div className="relative w-full h-[200px] mx-auto rounded-[15px] overflow-hidden border border-black/10">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-[200px] object-cover"
                  />
                  <div className="absolute bottom-2 text-xs text-white bg-black bg-opacity-70 py-1 text-center rounded-md left-3 right-3">
                    Position the skin area within the camera frame
                  </div>
                </div>
                <canvas
                  ref={canvasRef}
                  width="200"
                  height="150"
                  className="hidden"
                />
                <div className="mt-3 flex justify-center items-center gap-3">
                  <button
                    onClick={toggleCamera}
                    className="rounded-full w-10 h-10 border border-black/50 flex justify-center items-center text-xl"
                    title="Switch Camera"
                  >
                    <img
                      src={RefreshIcon}
                      alt="Toggle Camera"
                      className={`w-5 h-5 ${
                        spinning
                          ? spinDirection === "clockwise"
                            ? "spin-cw"
                            : "spin-ccw"
                          : ""
                      }`}
                    />
                  </button>
                  <button
                    onClick={handleCapture}
                    className="rounded-full bg-secondary text-xs w-14 h-14 flex justify-center items-center"
                  >
                    <img src={CaptureIcon} className="w-5 h-5" alt="Capture" />
                  </button>
                  <button
                    onClick={() => setShowCamera(false)}
                    className="rounded-full w-10 h-10 border border-black/50 flex justify-center items-center"
                  >
                    <img src={CloseIcon} className="w-3 h-3" alt="Close" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white px-6 pb-24 pt-6 mt-4">
        <AnimationOnScroll animateIn="animate__fadeInUp" duration={0.65}>
          <h3 className="text-lg text-primary mb-5 font-medium">
            Select body area
          </h3>
        </AnimationOnScroll>
        <div className="flex gap-2 flex-row flex-wrap">
          {options.map((option) => {
            const isSelected = selected === option.id;
            return (
              <AnimationOnScroll
                className="w-[17.7%]"
                animateIn="animate__fadeIn"
                duration={0.65}
              >
                <label
                  key={option.id}
                  htmlFor={option.id}
                  className="flex flex-col w-full justify-center items-center mb-1"
                >
                  <input
                    type="radio"
                    id={option.id}
                    name="areaType"
                    value={option.id}
                    className="hidden"
                    checked={isSelected}
                    onChange={() => setSelected(option.id)}
                  />
                  <div
                    className={`w-full py-3 rounded-[10px] transition-all ${
                      isSelected
                        ? "border-[#8AB2DF] border bg-[#F3F9FF]"
                        : "border-[#EBEBEB] border bg-[#F6F6F6]"
                    }`}
                  >
                    <img
                      src={option.image}
                      alt={option.label}
                      className="w-8 h-8 mx-auto"
                    />
                  </div>
                  <div
                    className={`mt-1 py-2 text-center text-sm transition-all ${
                      isSelected ? "text-blue-600" : "text-primary"
                    }`}
                  >
                    {option.label}
                  </div>
                </label>
              </AnimationOnScroll>
            );
          })}
        </div>

        <button
          onClick={handleAnalyze}
          disabled={!selected || loading}
          className={`mt-3 px-4 w-9/12 h-12 mx-auto text-sm font-medium rounded-full flex items-center justify-center transition ${
            selected
              ? "bg-[#1E2127] hover:bg-[#1E2127] text-white"
              : "bg-[#989898] cursor-not-allowed text-white/75"
          }`}
        >
          Analyze Skin
          {loading && (
            <svg
              className="animate-spin h-5 w-5 text-white ml-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
        </button>
        {error && (
          <p className="text-red-600 text-xxs mt-1 font-medium text-center">
            {error}
          </p>
        )}
      </div>
    </section>
  );
};

export default SkinAnalyzer;
